import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";
import { v4 } from "uuid";
import { fetchGoogleDriveConnection } from "../cores/googleDrive/connectApi";
import { PostImage } from "../cores/socialPost/interfaces/postImage";
import { GoogleDriveConnectModal } from "../ui/component/socialPost/modals/GoogleDriveConnectModal";
import { GoogleDriveFilePickerModal } from "../ui/component/socialPost/modals/GoogleDriveFilePickerModal";

type DriveModal = "connect" | "picker" | null;

type Options = {
  companyId?: string;
  onFilesSelected: (images: PostImage[]) => void;
};

export function useGoogleDriveImagePicker({ companyId, onFilesSelected }: Options) {
  const intl = useIntl();
  const [driveModal, setDriveModal] = useState<DriveModal>(null);

  const closeDriveModals = useCallback(() => {
    setDriveModal(null);
  }, []);

  const openGoogleDriveImages = useCallback(async () => {
    if (!companyId) return;
    try {
      const connection = await fetchGoogleDriveConnection(companyId);
      setDriveModal(connection ? "picker" : "connect");
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message ||
          intl.formatMessage({ id: "app.googleDrive.connectError" })
      );
    }
  }, [companyId, intl]);

  const handleDriveFilesSelected = useCallback(
    async (
      files: Array<{ name: string; mimeType: string; data: string; size?: number }>
    ) => {
      const mapped: PostImage[] = files.map((file) => ({
        id: v4(),
        name: file.name,
        type: file.mimeType,
        size: file.size || 0,
        data: file.data,
        lastModified: Date.now(),
        width: 0,
        height: 0,
        aspectRatio: "1:1",
      }));
      onFilesSelected(mapped);
      closeDriveModals();
    },
    [closeDriveModals, onFilesSelected]
  );

  const googleDriveModals =
    companyId && driveModal ? (
      <>
        <GoogleDriveConnectModal
          isOpen={driveModal === "connect"}
          toggle={closeDriveModals}
          companyId={companyId}
          onConnected={() => setDriveModal("picker")}
        />
        <GoogleDriveFilePickerModal
          isOpen={driveModal === "picker"}
          toggle={closeDriveModals}
          companyId={companyId}
          mode="image"
          onFilesSelected={handleDriveFilesSelected}
          onSwitchAccount={() => setDriveModal("connect")}
        />
      </>
    ) : null;

  return { openGoogleDriveImages, googleDriveModals };
}
